/**
 * The one fetch every generated API call goes through (orval's mutator).
 *
 * - Same-origin cookies carry the session (`stockvol_session`, HttpOnly). Every mutating request
 *   (anything but GET/HEAD/OPTIONS) also sends the CSRF token from the `stockvol_csrf` cookie in
 *   `X-CSRF-Token` (double submit; the API refuses a mutation without it).
 * - A 401 tells the app the session is gone (`onUnauthenticated`), then fails like any error.
 * - Failures throw `ApiError` with the API's `{detail, code}` body (and any extra keys, e.g. a
 *   CSV's bad lines); success resolves to `{data, status, headers}` as orval's types expect.
 * - JSON bodies are parsed; anything else (XLSX, PDF, raster `.bin`) comes back as a `Blob`.
 */

export const CSRF_COOKIE = "stockvol_csrf";
export const CSRF_HEADER = "X-CSRF-Token";
const SAFE = new Set(["GET", "HEAD", "OPTIONS"]);

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;
  readonly body: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    const detail = body["detail"];
    const code = typeof body["code"] === "string" ? body["code"] : `http_${status}`;
    super(typeof detail === "string" ? detail : `${code} (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.body = body;
  }
}

type Listener = () => void;
let unauthenticated: Listener | null = null;

/** Called on every 401 (the app logs out and shows the login page). */
export function onUnauthenticated(fn: Listener | null): void {
  unauthenticated = fn;
}

export function readCookie(name: string, cookies: string = document.cookie): string | null {
  for (const part of cookies.split(";")) {
    const [k, ...v] = part.trim().split("=");
    if (k === name) return decodeURIComponent(v.join("="));
  }
  return null;
}

async function bodyOf(r: Response): Promise<unknown> {
  if (r.status === 204 || r.status === 304) return undefined;
  const type = r.headers.get("content-type") ?? "";
  if (type.includes("application/json")) return r.json();
  return r.blob();
}

export async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  if (!SAFE.has(method)) {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers.set(CSRF_HEADER, token);
  }
  if (!headers.has("Accept")) headers.set("Accept", "application/json, */*");
  // absolute against the page (the generated paths are `/api/...`; tests run outside a browser)
  const target = typeof location !== "undefined" ? new URL(url, location.origin).toString() : url;
  const r = await fetch(target, { ...options, method, headers, credentials: "same-origin" });
  const data = await bodyOf(r);
  if (!r.ok) {
    if (r.status === 401) unauthenticated?.();
    const body =
      data !== null && typeof data === "object" && !(data instanceof Blob)
        ? (data as Record<string, unknown>)
        : { detail: r.statusText };
    throw new ApiError(r.status, body);
  }
  return { data, status: r.status, headers: r.headers } as T;
}

export default apiFetch;
